import { useState, useEffect, useCallback } from 'react';
import type { RiderType } from '../types/riderInfoDetail.types';
import { riderInfoDetailApi } from '../api/riderInfoDetailApi';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';

export const useRiderTypeManagement = (riderId?: string, initialRiderTypeCode?: string) => {
    const { showToast } = useToast();
    const [riderTypes, setRiderTypes] = useState<RiderType[]>([]);
    const [selectedRiderType, setSelectedRiderType] = useState('');
    const [initialRiderType, setInitialRiderType] = useState('');
    const [riderTypeLoading, setRiderTypeLoading] = useState(false);

    useEffect(() => {
        if (initialRiderTypeCode !== undefined) {
            setSelectedRiderType(initialRiderTypeCode);
            setInitialRiderType(initialRiderTypeCode);
        }
    }, [initialRiderTypeCode]);

    const fetchRiderTypes = useCallback(async () => {
        try {
            setRiderTypeLoading(true);
            if (!riderId) { setRiderTypes([]); return; }
            const list = await riderInfoDetailApi.getRiderTypes(riderId);
            setRiderTypes(list);
        } catch {
            setRiderTypes([]);
        } finally {
            setRiderTypeLoading(false);
        }
    }, [riderId]);

    useEffect(() => {
        fetchRiderTypes();
    }, [fetchRiderTypes]);

    const handleRiderTypeChange = useCallback((value: string) => {
        setSelectedRiderType(value);
    }, []);

    const handleSaveRiderType = useCallback(async () => {
        if (!riderId) {
            showToast('라이더 ID가 없습니다.', { type: ToastType.ERROR });
            return;
        }
        if (!selectedRiderType) {
            showToast('라이더 유형을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (selectedRiderType === initialRiderType) {
            showToast('변경된 라이더 유형이 없습니다.', { type: ToastType.WARNING });
            return;
        }
        try {
            await riderInfoDetailApi.updateRiderType({ riderId, riderType: selectedRiderType });
            showToast('라이더 유형이 저장되었습니다.', { type: ToastType.SUCCESS });
            setInitialRiderType(selectedRiderType);
        } catch {
            showToast('라이더 유형 저장에 실패했습니다. 다시 시도해주세요.', { type: ToastType.ERROR });
        }
    }, [riderId, selectedRiderType, initialRiderType, showToast]);

    return { riderTypes, riderTypeLoading, selectedRiderType, handleRiderTypeChange, handleSaveRiderType };
};
